"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql, prefetch, useQuery } from "@opensea/graphql";
import { EmptyState } from "@opensea/ui-kit";
import { bn, CurrencyStatsSortBy, normalizeAddress } from "@opensea/utils";
import { useCallback, useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useIsOwnProfileAddress, useProfileAddress } from "@/components/profile/ProfileAddressProvider";
import { useHasSearchFilters } from "@/components/search/hooks";
import { withSuspense } from "@/components/suspense/WithSuspense";
import { QUERY_PARAM_KEYS } from "@/hooks/query-param/keys";
import { useChainsQueryParam } from "@/hooks/query-param/use-chains-query-param";
import { useCurrencyCategoryQueryParam } from "@/hooks/query-param/use-currency-category-query-param";
import { useCurrencyStatsSortByQueryParam } from "@/hooks/query-param/use-currency-stats-sort-by-query-param";
import { useHasMerchandisingQueryParam } from "@/hooks/query-param/use-has-merchandising-query-param";
import { useHasNftQueryParam } from "@/hooks/query-param/use-has-nft-query-param";
import { usePriceQueryParam } from "@/hooks/query-param/use-price-query-param";
import { useSearchQueryQueryParam } from "@/hooks/query-param/use-search-query-query-param";
import { useSortDirectionQueryParam } from "@/hooks/query-param/use-sort-direction-query-param";
import { useStatusQueryParam } from "@/hooks/query-param/use-status-query-param";
import { useIsProfileTokensPaginationEnabled } from "@/hooks/use-flags";
import { useSubscription } from "@/hooks/use-subscription";
import { topStickyVariants } from "@/styles/utils/sticky";
import { CurrencyStatsTable } from "../CurrencyStatsTable";
import { getCurrencySortBy } from "../CurrencyStatsTable/utils";
import { ProfileCurrencyStatsTablePaginated } from "./ProfileCurrencyStatsTablePaginated";
import { ProfileCurrencyStatsTableRowLegacy } from "./ProfileCurrencyStatsTableRow.legacy";
import { ProfileCurrencyStatsTableRowLegacyFragment } from "./ProfileCurrencyStatsTableRow.legacy.fragment";
import { ProfileCurrencyStatsTableSkeleton } from "./ProfileCurrencyStatsTableSkeleton";
import { ProfileTokensActionBar } from "./ProfileTokensActionBar/ProfileTokensActionBar";
import { ProfileTokensCountProvider } from "./ProfileTokensCountProvider";
import { ProfileTokensSelectionProvider, useProfileTokensSelectionStore } from "./ProfileTokensSelectionProvider";
const query = graphql(`
    query ProfileCurrencyStatsTableQuery(
      $address: Address!
      $filter: CurrenciesFilter!
      $sort: CurrenciesSort
    ) {
      addressBalances(address: $address, filter: $filter, sort: $sort) {
        usdValue
        currency {
          id
          name
          symbol
          usdPrice
          stats {
            marketCapUsd
            oneDay {
              volume
              priceChange
            }
            sevenDay {
              priceChange
            }
          }
        }
        ...ProfileCurrencyStatsTableRowLegacy
      }
    }
  `, [ProfileCurrencyStatsTableRowLegacyFragment]);
const subscriptionQuery = graphql(`
    subscription ProfileCurrencyStatsTableSubscription(
      $address: Address!
      $filter: CurrenciesFilter!
    ) {
      currencyOwnershipUpdates(address: $address, filter: $filter) {
        quantity
        usdValue
        currency {
          id
          name
          symbol
          usdPrice
          stats {
            marketCapUsd
            oneDay {
              volume
              priceChange
            }
            sevenDay {
              priceChange
            }
          }
        }
        ...ProfileCurrencyStatsTableRowLegacy
      }
    }
  `, [ProfileCurrencyStatsTableRowLegacyFragment]);
const DEFAULT_SORT = {
  by: CurrencyStatsSortBy.USD_VALUE,
  direction: "DESC"
};
function ProfileCurrencyStatsTableBase(t0) {
  const $ = _c(72);
  let rest;
  if ($[0] !== t0) {
    const {
      persona: _persona,
      ...t1
    } = t0;
    rest = t1;
    $[0] = t0;
    $[1] = rest;
  } else {
    rest = $[1];
  }
  const address = useProfileAddress();
  const isOwnProfileAddress = useIsOwnProfileAddress();
  const hasFilters = useHasSearchFilters();
  const {
    searchQuery
  } = useSearchQueryQueryParam();
  const {
    chains
  } = useChainsQueryParam();
  const {
    category
  } = useCurrencyCategoryQueryParam();
  const {
    price: marketCap
  } = usePriceQueryParam(QUERY_PARAM_KEYS.marketCap);
  const {
    hasNft
  } = useHasNftQueryParam();
  const {
    hasMerchandising
  } = useHasMerchandisingQueryParam();
  const {
    statuses: rawStatuses,
    removeStatus
  } = useStatusQueryParam();
  let t1;
  if ($[2] !== isOwnProfileAddress || $[3] !== rawStatuses) {
    t1 = isOwnProfileAddress ? rawStatuses : rawStatuses.filter(_temp);
    $[2] = isOwnProfileAddress;
    $[3] = rawStatuses;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const statuses = t1;
  let t2;
  let t3;
  if ($[5] !== isOwnProfileAddress || $[6] !== rawStatuses || $[7] !== removeStatus) {
    t2 = () => {
      if (!isOwnProfileAddress && rawStatuses.includes("hidden")) {
        removeStatus("hidden");
      }
    };
    t3 = [isOwnProfileAddress, rawStatuses, removeStatus];
    $[5] = isOwnProfileAddress;
    $[6] = rawStatuses;
    $[7] = removeStatus;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  const {
    sortBy
  } = useCurrencyStatsSortByQueryParam(DEFAULT_SORT.by);
  const {
    sortDirection
  } = useSortDirectionQueryParam(DEFAULT_SORT.direction);
  let t4;
  if ($[10] !== sortBy) {
    t4 = getCurrencySortBy(sortBy);
    $[10] = sortBy;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== sortDirection || $[13] !== t4) {
    t5 = {
      direction: sortDirection,
      by: t4
    };
    $[12] = sortDirection;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const sort = t5;
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const [balances, setBalances] = useState(t6);
  const [setAllTokens] = useProfileTokensSelectionStore(useShallow(_temp2));
  const t7 = chains.length > 0 ? chains : undefined;
  let t8;
  if ($[16] !== marketCap) {
    t8 = marketCap ? {
      min: marketCap.min,
      max: marketCap.max
    } : undefined;
    $[16] = marketCap;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = hasNft || undefined;
  const t10 = hasMerchandising || undefined;
  let t11;
  if ($[18] !== isOwnProfileAddress || $[19] !== statuses) {
    t11 = isOwnProfileAddress ? statuses.includes("hidden") : undefined;
    $[18] = isOwnProfileAddress;
    $[19] = statuses;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== category || $[22] !== t10 || $[23] !== t11 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t12 = {
      chains: t7,
      coinGeckoCategory: category,
      marketCapRange: t8,
      hasNft: t9,
      hasMerchandising: t10,
      hiddenOnly: t11
    };
    $[21] = category;
    $[22] = t10;
    $[23] = t11;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const filter = t12;
  let t13;
  if ($[28] !== address || $[29] !== filter || $[30] !== sort) {
    t13 = {
      address,
      sort,
      filter
    };
    $[28] = address;
    $[29] = filter;
    $[30] = sort;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const t14 = !address;
  let t15;
  if ($[32] !== t13 || $[33] !== t14) {
    t15 = {
      query,
      variables: t13,
      pause: t14
    };
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const [t16, refetch] = useQuery(t15);
  const {
    data,
    error,
    fetching
  } = t16;
  let t17;
  if ($[35] !== refetch) {
    t17 = () => {
      refetch({
        requestPolicy: "network-only"
      });
    };
    $[35] = refetch;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const refetchData = t17;
  let t18;
  let t19;
  if ($[37] !== data || $[38] !== isOwnProfileAddress || $[39] !== setAllTokens) {
    t18 = function setFetchedBalances() {
      if (data) {
        setBalances(data.addressBalances);
        if (isOwnProfileAddress) {
          setAllTokens(data.addressBalances);
        }
      }
    };
    t19 = [data, isOwnProfileAddress, setAllTokens];
    $[37] = data;
    $[38] = isOwnProfileAddress;
    $[39] = setAllTokens;
    $[40] = t18;
    $[41] = t19;
  } else {
    t18 = $[40];
    t19 = $[41];
  }
  useEffect(t18, t19);
  let t20;
  if ($[42] !== address || $[43] !== filter) {
    t20 = {
      address,
      filter
    };
    $[42] = address;
    $[43] = filter;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const t21 = !address;
  let t22;
  if ($[45] !== t20 || $[46] !== t21) {
    t22 = {
      query: subscriptionQuery,
      variables: t20,
      pause: t21
    };
    $[45] = t20;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== isOwnProfileAddress || $[49] !== setAllTokens || $[50] !== sort) {
    t23 = newData => {
      setBalances(prevBalances => {
        let newBalances;
        const updateData = newData.currencyOwnershipUpdates;
        if (bn(updateData.quantity).isZero()) {
          newBalances = prevBalances.filter(balance => balance.currency.id !== updateData.currency.id);
        } else {
          if (prevBalances.find(balance_1 => balance_1.currency.id === updateData.currency.id)) {
            newBalances = prevBalances.map(balance_0 => balance_0.currency.id === updateData.currency.id ? updateData : balance_0);
          } else {
            newBalances = [...prevBalances, updateData];
          }
        }
        const sortedBalances = newBalances.sort(balanceSort(sort));
        if (isOwnProfileAddress) {
          setAllTokens(sortedBalances);
        }
        return sortedBalances;
      });
    };
    $[48] = isOwnProfileAddress;
    $[49] = setAllTokens;
    $[50] = sort;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  useSubscription(t22, t23);
  let t24;
  if ($[52] !== balances || $[53] !== searchQuery) {
    let t25;
    if ($[55] !== searchQuery) {
      t25 = item => item.currency.name.toLowerCase().includes(searchQuery?.toLowerCase() ?? "") || item.currency.symbol.toLowerCase().includes(searchQuery?.toLowerCase() ?? "");
      $[55] = searchQuery;
      $[56] = t25;
    } else {
      t25 = $[56];
    }
    t24 = balances.filter(t25);
    $[52] = balances;
    $[53] = searchQuery;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  const tableItems = t24;
  if (error || tableItems.length === 0) {
    const t25 = error ? "no-data" : hasFilters ? "no-results-with-filters" : "no-results";
    let t26;
    if ($[57] !== t25) {
      t26 = <EmptyState size="lg" variant={t25} />;
      $[57] = t25;
      $[58] = t26;
    } else {
      t26 = $[58];
    }
    return t26;
  }
  const t25 = tableItems.length;
  const t26 = hasFilters ? "after-hero-header-search-pills" : "after-hero-header-toolbar";
  let t27;
  if ($[59] !== t26) {
    t27 = topStickyVariants({
      variant: t26,
      tableHeader: true
    });
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== rest || $[62] !== t27 || $[63] !== tableItems) {
    t28 = <CurrencyStatsTable defaultSortBy={DEFAULT_SORT.by} headerClassName={t27} itemKey={_temp3} items={tableItems} profile={true} renderRow={CurrencyStatsTableRow} {...rest} />;
    $[61] = rest;
    $[62] = t27;
    $[63] = tableItems;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== refetchData) {
    t29 = <ProfileTokensActionBar onRefetch={refetchData} />;
    $[65] = refetchData;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== fetching || $[68] !== t28 || $[69] !== t29 || $[70] !== tableItems.length) {
    t30 = <ProfileTokensCountProvider fetching={fetching} hasMore={false} totalCount={t25}>{t28}{t29}</ProfileTokensCountProvider>;
    $[67] = fetching;
    $[68] = t28;
    $[69] = t29;
    $[70] = tableItems.length;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  return t30;
}
function _temp3(item_0) {
  return item_0?.currency.id;
}
function _temp2(state) {
  return [state.setAllTokens];
}
function _temp(s) {
  return s !== "hidden";
}
export const ProfileCurrencyStatsTable = withSuspense(function ProfileCurrencyStatsTable(props) {
  const isOwnProfileAddress = useIsOwnProfileAddress();
  const isPaginationEnabled = useIsProfileTokensPaginationEnabled();
  if (isPaginationEnabled) {
    return <ProfileCurrencyStatsTablePaginated {...props} />;
  }
  if (isOwnProfileAddress) {
    return <ProfileTokensSelectionProvider>
          <ProfileCurrencyStatsTableBase {...props} />
        </ProfileTokensSelectionProvider>;
  }
  return <ProfileCurrencyStatsTableBase {...props} />;
}, {
  fallback: ({
    persona
  }) => <ProfileCurrencyStatsTableSkeleton persona={persona} />,
  errorFallback: () => <EmptyState size="lg" variant="no-data" />,
  ssr: false
});
function CurrencyStatsTableRow(t0) {
  const $ = _c(3);
  const {
    item,
    index
  } = t0;
  const t1 = index + 1;
  let t2;
  if ($[0] !== item || $[1] !== t1) {
    t2 = <ProfileCurrencyStatsTableRowLegacy currencyBalance={item} rank={t1} />;
    $[0] = item;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export const prefetchProfileCurrencyStatsTable = (address, client) => {
  prefetch(client, query, {
    address: normalizeAddress(address),
    filter: {},
    sort: {
      by: getCurrencySortBy(DEFAULT_SORT.by),
      direction: DEFAULT_SORT.direction
    }
  });
};
const balanceSort = sort => (a, b) => {
  switch (sort.by) {
    case "USD_VALUE":
      return sort.direction === "DESC" ? bn(b.usdValue).minus(bn(a.usdValue)).toNumber() : bn(a.usdValue).minus(bn(b.usdValue)).toNumber();
    case "MARKET_CAP":
      {
        return sort.direction === "DESC" ? (b.currency.stats?.marketCapUsd ?? 0) - (a.currency.stats?.marketCapUsd ?? 0) : (a.currency.stats?.marketCapUsd ?? 0) - (b.currency.stats?.marketCapUsd ?? 0);
      }
    case "PRICE":
      {
        return sort.direction === "DESC" ? bn(b.currency.usdPrice).minus(bn(a.currency.usdPrice)).toNumber() : bn(a.currency.usdPrice).minus(bn(b.currency.usdPrice)).toNumber();
      }
    case "ONE_DAY_VOLUME":
      {
        return sort.direction === "DESC" ? bn(b.currency.stats?.oneDay.volume ?? 0).minus(bn(a.currency.stats?.oneDay.volume ?? 0)).toNumber() : bn(a.currency.stats?.oneDay.volume ?? 0).minus(bn(b.currency.stats?.oneDay.volume ?? 0)).toNumber();
      }
    case "ONE_DAY_PRICE_CHANGE":
      {
        return sort.direction === "DESC" ? bn(b.currency.stats?.oneDay.priceChange ?? 0).minus(bn(a.currency.stats?.oneDay.priceChange ?? 0)).toNumber() : bn(a.currency.stats?.oneDay.priceChange ?? 0).minus(bn(b.currency.stats?.oneDay.priceChange ?? 0)).toNumber();
      }
    case "SEVEN_DAY_PRICE_CHANGE":
      {
        return sort.direction === "DESC" ? bn(b.currency.stats?.sevenDay.priceChange ?? 0).minus(bn(a.currency.stats?.sevenDay.priceChange ?? 0)).toNumber() : bn(a.currency.stats?.sevenDay.priceChange ?? 0).minus(bn(b.currency.stats?.sevenDay.priceChange ?? 0)).toNumber();
      }
    default:
      return 0;
  }
};